import React, { FC, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { QuestionDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import { T } from 'src/shared/helpers';
import {
    AxisIcon,
    CollapseIcon,
    ExplodeIcon,
    FrameSelectionIcon as ResetCameraIcon,
    FullscreenExitIcon,
    FullscreenIcon,
    GridIcon,
    MoveToolIcon as TranslateIcon,
    PreviewModeIcon,
    RotateToolIcon as RotateIcon,
    SaveAssetIcon as SavePositionIcon,
    ScaleToolIcon as ScaleIcon,
    SelectToolIcon as SelectionIcon,
    SwapIcon as SwitchIcon,
    TrashIcon as RemoveIcon
} from '../../../../../../widgets/icons/scene';
import SceneEditorStatusbar from './SceneEditorStatusbar';
import { MeasurementUnit } from '../../../configurator-scene-editor/useWorkspacePreferences';
import { applySelectionHighlight, SelectionHighlight } from '../../../configurator-scene-editor/selectionHighlight';

const ToolbarLayer = styled.div`
    position: absolute;
    inset: 0;
    z-index: 10;
    pointer-events: none;

    > * {
        pointer-events: auto;
    }
`;

const ToolGroup = styled.div`
    position: absolute;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px;
    background: ${(props) => props.theme.app.backgroundColor};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 999px;
    backdrop-filter: blur(16px) saturate(1.4);
`;

const TransformToolbar = styled(ToolGroup)`
    top: 14px;
    left: 50%;
    transform: translateX(-50%);
`;

// L'angolo in alto a destra risponde alla domanda «da dove guardo»: ci sta «inquadra», e
// nient'altro. È un'AZIONE, e in mezzo agli interruttori era l'unica cosa che, premuta, non
// restava premuta.
const CameraCorner = styled(ToolGroup)`
    top: 18px;
    right: 15px;
    flex-direction: column;
`;

// Il fianco destro, a metà altezza: COSA SI VEDE nel palco. Quattro interruttori con una
// grammatica sola — ognuno dice se una cosa compare o no.
const ViewToolbar = styled(ToolGroup)`
    top: 50%;
    right: 15px;
    flex-direction: column;
    transform: translateY(-50%);
`;

const ExplodeToolbar = styled(ToolGroup)`
    top: 50%;
    left: 15px;
    flex-direction: column;
    transform: translateY(-50%);
`;

const ExplodeAndCollapsePanel = styled.div`
    position: absolute;
    top: 50%;
    left: 70px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px;
    background: ${(props) => props.theme.app.backgroundColor};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 12px;
    transform: translateY(-50%);
`;

const PreviewToolbar = styled(ToolGroup)`
    right: 15px;
    bottom: 38px;
`;

const ToolSeparator = styled.span`
    width: 1px;
    height: 20px;
    margin: 0 2px;
    background: ${(props) => props.theme.app.borderColor};
`;

const ToolButton = styled.button<{ $isActive?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    color: ${(props) => (props.$isActive ? props.theme.app.secondary : props.theme.app.textColor)};
    background: ${(props) => (props.$isActive ? props.theme.app.pageBackgroundColor : 'transparent')};
    cursor: pointer;

    &:hover:not(:disabled),
    &:focus-visible {
        color: ${(props) => props.theme.app.secondary};
        background: ${(props) => props.theme.app.pageBackgroundColor};
        outline: none;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

enum EditingMode {
    None = 'None',
    Translate = 'Translate',
    Rotate = 'Rotate',
    Scaling = 'Scaling'
}

interface SceneEditorToolbarProps {
    sceneEditor?: any;
    stageRef?: RefObject<HTMLDivElement | null>;
    /* IL FONDALE LO TIENE CHI STA SOPRA, non questa barra: la sezione World lo accende entrando, e
       due posti che decidono la stessa cosa si contraddicono al primo di loro che non lo sa. Qui
       resta il tasto — è un modo di GUARDARE la scena, e i modi di guardare stanno sul palco. */
    previewActive?: boolean;
    onPreviewToggle?: () => void;
    measurementUnit?: MeasurementUnit;
    onMeasurementUnitChange?: (unit: MeasurementUnit) => void;
    selectionHighlight?: SelectionHighlight;
}

const SceneEditorToolbar: FC<SceneEditorToolbarProps> = ({
    sceneEditor,
    stageRef,
    previewActive = false,
    onPreviewToggle,
    measurementUnit = 'm',
    onMeasurementUnitChange,
    selectionHighlight
}) => {
    const [currentMode, setCurrentMode] = useState<EditingMode>(EditingMode.None);
    const [showGrid, setShowGrid] = useState(true);
    const [showAxes, setShowAxes] = useState(false);
    const [showSelection, setShowSelection] = useState(true);
    const [showExplodePanel, setShowExplodePanel] = useState(false);
    const [showCollapsePanel, setShowCollapsePanel] = useState(false);
    const [areExplodePositionsAvailable, setAreExplodePositionsAvailable] = useState(false);
    const [areCollapsePositionsAvailable, setAreCollapsePositionsAvailable] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const viewStateInitialized = useRef(false);
    const { showDialog, closeDialog } = useDialogManager();

    const checkPositionsAvailability = useCallback(() => {
        const zkScene = sceneEditor?.zkScene;
        setAreExplodePositionsAvailable(zkScene?.getFirstExplodedMeshLocation?.() != null);
        setAreCollapsePositionsAvailable(zkScene?.getFirstCollapsedMeshLocation?.() != null);
    }, [sceneEditor]);

    useEffect(() => {
        if (!sceneEditor) return;
        if (!viewStateInitialized.current) {
            // SI ENTRA CON LA GRIGLIA ACCESA, sempre (utente, 2026-09-15): è il riferimento con cui si
            // legge dove sta e quanto è grande ciò che si guarda. Si accende sul motore e non solo sul
            // tastino — un tastino acceso su una scena senza griglia direbbe una cosa falsa — e chi la
            // spegne la spegne per questa visita: alla prossima entrata torna.
            sceneEditor.showGround = true;
            sceneEditor.showHideGround?.(true);
            setShowGrid(true);
            setShowAxes(sceneEditor.showAxes ?? false);
            viewStateInitialized.current = true;
        }
        checkPositionsAvailability();
    }, [checkPositionsAvailability, sceneEditor]);

    // L'ALONE LO DECIDE SETTINGS, NON LO STRUMENTO: vedi `useSelectionHighlight`.
    useEffect(() => {
        if (selectionHighlight) applySelectionHighlight(sceneEditor, selectionHighlight);
    }, [sceneEditor, selectionHighlight]);

    useEffect(() => {
        const onFullscreenChange = () => setIsFullscreen(document.fullscreenElement === stageRef?.current);
        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, [stageRef]);

    const selectMode = useCallback(
        (mode: EditingMode) => {
            setCurrentMode(mode);
            setShowSelection(mode === EditingMode.None);
            sceneEditor?.showHideEditControl?.(mode !== EditingMode.None);
            sceneEditor?.setEditingMode?.(mode);
        },
        [sceneEditor]
    );

    const handleSelectionClick = useCallback(() => selectMode(EditingMode.None), [selectMode]);
    const handleTranslateClick = useCallback(() => selectMode(EditingMode.Translate), [selectMode]);
    const handleRotateClick = useCallback(() => selectMode(EditingMode.Rotate), [selectMode]);
    const handleScaleClick = useCallback(() => selectMode(EditingMode.Scaling), [selectMode]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null;
            if (event.metaKey || event.ctrlKey || event.altKey || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '') || target?.isContentEditable)
                return;

            const handlers: Record<string, () => void> = {
                v: handleSelectionClick,
                w: handleTranslateClick,
                e: handleRotateClick,
                r: handleScaleClick
            };
            const handler = handlers[event.key.toLowerCase()];
            if (handler) {
                event.preventDefault();
                handler();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleSelectionClick, handleTranslateClick, handleRotateClick, handleScaleClick]);

    const handleAxisClick = () => {
        const next = !showAxes;
        if (typeof sceneEditor?.showHideAxes === 'function') {
            sceneEditor.showAxes = next;
            sceneEditor.showHideAxes(next);
        } else if (sceneEditor) {
            sceneEditor.showAxes = next;
        }
        setShowAxes(next);
    };

    const handleGridClick = () => {
        const next = !showGrid;
        if (typeof sceneEditor?.showHideGround === 'function') {
            sceneEditor.showGround = next;
            sceneEditor.showHideGround(next);
        } else if (sceneEditor) {
            sceneEditor.showGround = next;
        }
        setShowGrid(next);
    };

    const handleFrameClick = () => sceneEditor?.adjustCamera?.();

    const handleFullscreen = async () => {
        if (!stageRef?.current) return;
        if (document.fullscreenElement) {
            await document.exitFullscreen?.();
        } else {
            await stageRef.current.requestFullscreen?.();
        }
    };

    const handleExplodeClick = () => {
        setShowExplodePanel((value) => !value);
        setShowCollapsePanel(false);
    };

    const handleCollapseClick = () => {
        setShowCollapsePanel((value) => !value);
        setShowExplodePanel(false);
    };

    const confirm = (id: string, message: string, onYes: () => void) => {
        showDialog(
            id,
            <QuestionDialog
                title={T._('Are you sure?', 'Admin')}
                message={T._(message, 'Admin')}
                onYesClick={() => {
                    closeDialog(id);
                    onYes();
                }}
                onNoClick={() => closeDialog(id)}
            />
        );
    };

    const handleSaveExplode = () =>
        confirm('save-explode-dialog', 'Do you want save the exploded positions?', () => {
            sceneEditor?.saveExplodedMeshLocation?.();
            setAreExplodePositionsAvailable(true);
        });

    const handleRemoveExplode = () =>
        confirm('delete-explode-dialog', 'Do you want delete the exploded positions?', () => {
            const location = sceneEditor?.zkScene?.getFirstExplodedMeshLocation?.();
            if (location) sceneEditor.zkScene.get('meshLocations').remove(location);
            setAreExplodePositionsAvailable(false);
        });

    const handleSaveCollapse = () =>
        confirm('save-collapse-dialog', 'Do you want save the collapsed positions?', () => {
            sceneEditor?.saveCollapsedMeshLocation?.();
            setAreCollapsePositionsAvailable(true);
        });

    const handleRemoveCollapse = () =>
        confirm('delete-collapse-dialog', 'Do you want delete the collapsed positions?', () => {
            const location = sceneEditor?.zkScene?.getFirstCollapsedMeshLocation?.();
            if (location) sceneEditor.zkScene.get('meshLocations').remove(location);
            setAreCollapsePositionsAvailable(false);
        });

    if (!sceneEditor) return null;

    return (
        <ToolbarLayer>
            <TransformToolbar aria-label={T._('Transform tools', 'Admin')}>
                <ZTooltip title={T._('Select (V)', 'Admin')} position='bottom'>
                    <ToolButton $isActive={showSelection} onClick={handleSelectionClick} aria-label={T._('Select tool', 'Admin')} aria-pressed={showSelection}>
                        <SelectionIcon />
                    </ToolButton>
                </ZTooltip>
                <ToolSeparator />
                <ZTooltip title={T._('Move (W)', 'Admin')} position='bottom'>
                    <ToolButton
                        $isActive={currentMode === EditingMode.Translate}
                        onClick={handleTranslateClick}
                        aria-label={T._('Move tool', 'Admin')}
                        aria-pressed={currentMode === EditingMode.Translate}
                    >
                        <TranslateIcon />
                    </ToolButton>
                </ZTooltip>
                <ZTooltip title={T._('Rotate (E)', 'Admin')} position='bottom'>
                    <ToolButton
                        $isActive={currentMode === EditingMode.Rotate}
                        onClick={handleRotateClick}
                        aria-label={T._('Rotate tool', 'Admin')}
                        aria-pressed={currentMode === EditingMode.Rotate}
                    >
                        <RotateIcon />
                    </ToolButton>
                </ZTooltip>
                <ZTooltip title={T._('Scale (R)', 'Admin')} position='bottom'>
                    <ToolButton
                        $isActive={currentMode === EditingMode.Scaling}
                        onClick={handleScaleClick}
                        aria-label={T._('Scale tool', 'Admin')}
                        aria-pressed={currentMode === EditingMode.Scaling}
                    >
                        <ScaleIcon />
                    </ToolButton>
                </ZTooltip>
            </TransformToolbar>

            <CameraCorner aria-label={T._('Camera', 'Admin')}>
                <ZTooltip title={T._('Frame scene', 'Admin')} position='left'>
                    <ToolButton onClick={handleFrameClick} aria-label={T._('Frame scene', 'Admin')}>
                        <ResetCameraIcon />
                    </ToolButton>
                </ZTooltip>
            </CameraCorner>

            <ViewToolbar aria-label={T._('Viewport', 'Admin')}>
                <ZTooltip title={T._('Show or hide grid', 'Admin')} position='left'>
                    <ToolButton onClick={handleGridClick} $isActive={showGrid} aria-label={T._('Show or hide grid', 'Admin')} aria-pressed={showGrid}>
                        <GridIcon />
                    </ToolButton>
                </ZTooltip>
                <ZTooltip title={T._('Show or hide axes', 'Admin')} position='left'>
                    <ToolButton onClick={handleAxisClick} $isActive={showAxes} aria-label={T._('Show or hide axes', 'Admin')} aria-pressed={showAxes}>
                        <AxisIcon />
                    </ToolButton>
                </ZTooltip>
                {/* IL FONDALE STA IN BASSO A DESTRA, non fra questi: è l'interruttore «Preview mode», e
                    da lì commuta la scena intera invece di una cosa sola. Questi tre dicono cosa compare
                    nel palco; quello dice con quale dei due modi si disegna tutto.

                    LA ROTAZIONE AUTOMATICA NON STA PIÙ QUI. Questi tasti sono i modi di GUARDARE la
                    scena mentre ci si lavora — la griglia, il fondale, gli assi — e non si salvano con
                    il prodotto; la rotazione automatica invece è un'impostazione della camera, si
                    salva nella scena e si legge accanto a campo visivo e limiti, nel pannello che
                    apre «Free camera» della sezione Cameras. */}
            </ViewToolbar>

            <ExplodeToolbar aria-label={T._('Model state tools', 'Admin')}>
                <ZTooltip title={T._('Explode tools', 'Admin')} position='left'>
                    <ToolButton
                        $isActive={showExplodePanel}
                        onClick={handleExplodeClick}
                        aria-label={T._('Explode tools', 'Admin')}
                        aria-pressed={showExplodePanel}
                    >
                        <ExplodeIcon />
                    </ToolButton>
                </ZTooltip>
                <ZTooltip title={T._('Collapse tools', 'Admin')} position='left'>
                    <ToolButton
                        $isActive={showCollapsePanel}
                        onClick={handleCollapseClick}
                        aria-label={T._('Collapse tools', 'Admin')}
                        aria-pressed={showCollapsePanel}
                    >
                        <CollapseIcon />
                    </ToolButton>
                </ZTooltip>
            </ExplodeToolbar>

            {(showExplodePanel || showCollapsePanel) && (
                <ExplodeAndCollapsePanel>
                    {showExplodePanel && (
                        <>
                            <ZTooltip title={T._('Explode model', 'Admin')} position='left'>
                                <ToolButton
                                    disabled={!areExplodePositionsAvailable}
                                    onClick={() => sceneEditor.explodeMeshes()}
                                    aria-label={T._('Explode model', 'Admin')}
                                >
                                    <SwitchIcon />
                                </ToolButton>
                            </ZTooltip>
                            <ZTooltip title={T._('Save exploded position', 'Admin')} position='left'>
                                <ToolButton onClick={handleSaveExplode} aria-label={T._('Save exploded position', 'Admin')}>
                                    <SavePositionIcon />
                                </ToolButton>
                            </ZTooltip>
                            <ZTooltip title={T._('Remove exploded position', 'Admin')} position='left'>
                                <ToolButton
                                    disabled={!areExplodePositionsAvailable}
                                    onClick={handleRemoveExplode}
                                    aria-label={T._('Remove exploded position', 'Admin')}
                                >
                                    <RemoveIcon />
                                </ToolButton>
                            </ZTooltip>
                        </>
                    )}
                    {showCollapsePanel && (
                        <>
                            <ZTooltip title={T._('Collapse model', 'Admin')} position='left'>
                                <ToolButton
                                    disabled={!areCollapsePositionsAvailable}
                                    onClick={() => sceneEditor.collapseMeshes()}
                                    aria-label={T._('Collapse model', 'Admin')}
                                >
                                    <SwitchIcon />
                                </ToolButton>
                            </ZTooltip>
                            <ZTooltip title={T._('Save collapsed position', 'Admin')} position='left'>
                                <ToolButton onClick={handleSaveCollapse} aria-label={T._('Save collapsed position', 'Admin')}>
                                    <SavePositionIcon />
                                </ToolButton>
                            </ZTooltip>
                            <ZTooltip title={T._('Remove collapsed position', 'Admin')} position='left'>
                                <ToolButton
                                    disabled={!areCollapsePositionsAvailable}
                                    onClick={handleRemoveCollapse}
                                    aria-label={T._('Remove collapsed position', 'Admin')}
                                >
                                    <RemoveIcon />
                                </ToolButton>
                            </ZTooltip>
                        </>
                    )}
                </ExplodeAndCollapsePanel>
            )}

            <PreviewToolbar>
                {/* I DUE MODI DI DISEGNARE LA SCENA — vedi `useSceneBackground`. Premuto, la scena si
                    disegna come la vedrà chi compra: il suo environment, il pavimento, l'antialiasing e
                    nessun alone di selezione. È il gesto che per qualche settimana ha abitato «Preview»
                    in fascia, e torna qui perché guardare non è provare: la prova del cliente è un'altra
                    pagina, e quel tasto adesso porta lì. */}
                <ZTooltip title={T._('Preview mode', 'Admin')} position='top'>
                    <ToolButton
                        $isActive={previewActive}
                        onClick={() => onPreviewToggle?.()}
                        aria-label={T._('Preview mode', 'Admin')}
                        aria-pressed={previewActive}
                    >
                        <PreviewModeIcon />
                    </ToolButton>
                </ZTooltip>
                <ZTooltip title={T._(isFullscreen ? 'Exit fullscreen' : 'Fullscreen', 'Admin')} position='top'>
                    <ToolButton
                        $isActive={isFullscreen}
                        onClick={handleFullscreen}
                        aria-pressed={isFullscreen}
                        aria-label={T._(isFullscreen ? 'Exit fullscreen' : 'Fullscreen', 'Admin')}
                    >
                        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    </ToolButton>
                </ZTooltip>
            </PreviewToolbar>

            {onMeasurementUnitChange && (
                <SceneEditorStatusbar sceneEditor={sceneEditor} measurementUnit={measurementUnit} onMeasurementUnitChange={onMeasurementUnitChange} />
            )}
        </ToolbarLayer>
    );
};

export default SceneEditorToolbar;
